// A fixed-viewport, same-origin <iframe> scaled to fit its container, dressed
// as a small app window (chrome strip with three dots and a title). Built for
// the Academy's living player, where the real app runs inside the frame and
// the parent drives it over postMessage, but nothing here knows about scenes:
// it is a scaled frame plus a message channel.
//
//   const ref = useRef<StageFrameHandle>(null);
//   <StageFrame ref={ref} src="/cases?academy=…" title="Your first report"
//     onMessage={(data) => …} busy={waiting} busyLabel="Loading the app…">
//     {captionBar}   // overlaid on the stage, bottom-aligned, unscaled
//   </StageFrame>
//   ref.current?.postMessage({ type: "academy:play", stepIndex: 0 });
//
// Messages are accepted only from this origin AND from this frame's window.
// A skeleton covers the frame until its first message arrives (or while
// `busy`), since a same-origin app takes a moment to boot. The frame is not
// sandboxed: the app inside needs scripts and storage. Pointer events are off
// by default so a viewer cannot derail a scripted run; pass `interactive` to
// allow clicks.
import { forwardRef, ReactNode, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from "react";
import cn from "classnames";

export interface StageFrameHandle {
  postMessage: (message: unknown) => void;
  /** Reloads the frame at its current src. */
  reload: () => void;
  element: HTMLIFrameElement | null;
}

interface Props {
  src: string;
  title: string;
  /** Fixed viewport the page inside renders at; the frame scales to fit. */
  width?: number;
  height?: number;
  onMessage?: (data: unknown) => void;
  onLoad?: () => void;
  /** Show the skeleton again (a reload the parent asked for). */
  busy?: boolean;
  busyLabel?: string;
  interactive?: boolean;
  /** Overlaid on the stage area (captions, notices); not scaled. */
  children?: ReactNode;
  /**
   * Fill the parent (a flex column with a definite height, e.g. a fullscreen
   * player) instead of sizing to the width: the viewport is scaled to fit
   * both ways and centered, and the window chrome is dropped.
   */
  fill?: boolean;
  className?: string;
}

const StageFrame = forwardRef<StageFrameHandle, Props>(
  ({ src, title, width = 1440, height = 900, onMessage, onLoad, busy = false, busyLabel = "Loading…", interactive = false, children, className, fill = false }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [scale, setScale] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [heard, setHeard] = useState(false);
    const onMessageRef = useRef(onMessage);
    onMessageRef.current = onMessage;

    useImperativeHandle(
      ref,
      () => ({
        postMessage: (message: unknown) => {
          iframeRef.current?.contentWindow?.postMessage(message, window.location.origin);
        },
        reload: () => {
          const el = iframeRef.current;
          if (!el) return;
          setHeard(false);
          try {
            // Same origin, so the frame's own location is reachable.
            el.contentWindow?.location.reload();
          } catch {
            const current = el.getAttribute("src");
            if (current) el.setAttribute("src", current);
          }
        },
        element: iframeRef.current,
      }),
      [],
    );

    // Scale the fixed viewport to the container: by width, or (fill) to fit
    // inside it both ways and center it.
    useLayoutEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      const measure = () => {
        if (!fill) {
          setScale(Math.max(0.05, el.clientWidth / width));
          setOffset({ x: 0, y: 0 });
          return;
        }
        const s = Math.max(0.05, Math.min(el.clientWidth / width, el.clientHeight / height));
        setScale(s);
        setOffset({ x: Math.max(0, (el.clientWidth - width * s) / 2), y: Math.max(0, (el.clientHeight - height * s) / 2) });
      };
      measure();
      if (typeof ResizeObserver === "undefined") {
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
      }
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }, [width, height, fill]);

    const handleMessage = useCallback((event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;
      setHeard(true);
      onMessageRef.current?.(event.data);
    }, []);

    useEffect(() => {
      window.addEventListener("message", handleMessage);
      return () => window.removeEventListener("message", handleMessage);
    }, [handleMessage]);

    // A new src is a new boot.
    useEffect(() => {
      setHeard(false);
    }, [src]);

    const showSkeleton = busy || !heard;

    return (
      <div className={cn(fill ? "flex-1 min-h-0 flex flex-col bg-base-100 overflow-hidden" : "rounded-xl border border-base-300 bg-base-100 shadow-sm overflow-hidden", className)}>
        {/* Window chrome */}
        <div className={cn("items-center gap-2 px-3 h-8 border-b border-base-300 bg-base-200", fill ? "hidden" : "flex")}>
          <span className="flex items-center gap-1.5" aria-hidden>
            <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
            <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
          </span>
          <span className="flex-1 text-center text-xs text-base-content/60 truncate pr-10">{title}</span>
        </div>
        {/* Stage */}
        <div ref={containerRef} className={cn("relative w-full bg-base-200", fill && "flex-1 min-h-0")} style={fill ? undefined : { height: Math.round(height * scale) }}>
          <iframe
            ref={iframeRef}
            src={src}
            title={title}
            tabIndex={-1}
            onLoad={onLoad}
            className={cn("absolute top-0 left-0 border-0 bg-base-100", !interactive && "pointer-events-none")}
            style={{ width, height, transform: `scale(${scale})`, transformOrigin: "top left", left: offset.x, top: offset.y }}
          />
          {showSkeleton && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-base-200/90 backdrop-blur-[1px]" aria-live="polite">
              <div className="w-8 h-8 border-[3px] border-base-300 border-t-blue-500 rounded-full animate-spin" />
              <p className="text-sm text-base-content/60">{busyLabel}</p>
            </div>
          )}
          {children}
        </div>
      </div>
    );
  },
);
StageFrame.displayName = "StageFrame";

export default StageFrame;
